/**
 * AI FAQ API Mixin
 *
 * Handles communication with the AI FAQ backend service
 * Provides methods for asking questions and handling responses
 */

import axios from 'axios'

export default {
    data() {
        return {
            // API configuration
            apiConfig: {
                timeout: 30000, // 30 seconds
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            }
        }
    },

    methods: {
        /**
         * Ask a question to the AI FAQ service
         *
         * @param {string} question - The user's question
         * @param {number|string} occasionId - The occasion ID for context
         * @returns {Promise} API response
         */
        async askQuestion(question, occasionId, participantId) {
            const endpoint = this.apiEndpoint || '/api/v0/ai/ask-question'

            try {
                const response = await axios.post(endpoint, {
                    question: question.trim(),
                    occasion_id: parseInt(occasionId),
                    participant_id: parseInt(participantId)
                }, {
                    ...this.apiConfig,
                    // Add CSRF token if available (CakePHP pattern)
                    headers: {
                        ...this.apiConfig.headers,
                        'X-CSRF-Token': this.getCsrfToken()
                    }
                })

                // Handle CakeDC API response format: {status: "success", data: {...}}
                let actualData = response.data
                if (response.data && response.data.status === 'success' && response.data.data) {
                    actualData = response.data.data
                }

                if (actualData && actualData.success) {
                    return actualData
                } else {
                    const errorMsg = actualData && actualData.error ? actualData.error : 'Unknown error occurred'
                    throw new Error(errorMsg)
                }

            } catch (error) {
                // Enhanced error handling
                if (error.response) {
                    // Server responded with error status
                    const errorData = error.response.data
                    const enhancedError = new Error(errorData.error || 'Server error')
                    enhancedError.response = error.response
                    enhancedError.status = error.response.status
                    enhancedError.data = errorData
                    throw enhancedError
                } else if (error.request) {
                    // Network error
                    const networkError = new Error('Network error - please check your connection')
                    networkError.code = 'NETWORK_ERROR'
                    networkError.originalError = error
                    throw networkError
                } else {
                    // Other error
                    throw error
                }
            }
        },

        /**
         * Get CSRF token for CakePHP compatibility
         *
         * @returns {string|null} CSRF token
         */
        getCsrfToken() {
            // Try to get CSRF token from meta tag (common CakePHP pattern)
            const csrfMeta = document.querySelector('meta[name="csrf-token"]')
            if (csrfMeta) {
                return csrfMeta.getAttribute('content')
            }

            // Try to get from cookie (alternative pattern)
            const csrfCookie = this.getCookie('csrfToken')
            if (csrfCookie) {
                return csrfCookie
            }

            // Try to get from window global (if set by template)
            if (window.csrfToken) {
                return window.csrfToken
            }

            return null
        },

        /**
         * Get cookie value by name
         *
         * @param {string} name - Cookie name
         * @returns {string|null} Cookie value
         */
        getCookie(name) {
            const value = `; ${document.cookie}`
            const parts = value.split(`; ${name}=`)
            if (parts.length === 2) {
                return parts.pop().split(';').shift()
            }
            return null
        },

        /**
         * Validate question before sending to API
         *
         * @param {string} question - The question to validate
         * @returns {object} Validation result
         */
        validateQuestionForApi(question) {
            const trimmed = question.trim()

            if (!trimmed) {
                return {
                    valid: false,
                    error: 'Question cannot be empty'
                }
            }

            if (trimmed.length < 10) {
                return {
                    valid: false,
                    error: 'Question must be at least 10 characters long'
                }
            }

            if (trimmed.length > 500) {
                return {
                    valid: false,
                    error: 'Question must be no more than 500 characters long'
                }
            }

            // Check for suspicious patterns (basic XSS prevention)
            if (/<[^>]*>/.test(trimmed)) {
                return {
                    valid: false,
                    error: 'HTML tags are not allowed in questions'
                }
            }

            // Check for script injection patterns
            if (/javascript:|script:|eval\(|setTimeout\(|setInterval\(/i.test(trimmed)) {
                return {
                    valid: false,
                    error: 'Invalid characters detected in question'
                }
            }

            return {
                valid: true,
                question: trimmed
            }
        },

        /**
         * Format API error for user display
         *
         * @param {Error} error - The error object
         * @returns {object} Formatted error info
         */
        formatApiError(error) {
            let message = 'An unexpected error occurred'
            let type = 'general'
            let canRetry = true

            if (error.response) {
                const status = error.response.status
                const data = error.response.data

                switch (status) {
                    case 400:
                        message = data.error || 'Invalid request'
                        type = 'validation'
                        canRetry = false
                        break
                    case 404:
                        message = 'Event not found'
                        type = 'not-found'
                        canRetry = false
                        break
                    case 429:
                        message = 'Too many requests. Please wait before asking another question.'
                        type = 'rate-limit'
                        canRetry = false
                        break
                    case 500:
                        message = 'Server error. Please try again later.'
                        type = 'server'
                        canRetry = true
                        break
                    default:
                        message = data.error || `Server error (${status})`
                        type = 'server'
                        canRetry = true
                }
            } else if (error.code === 'NETWORK_ERROR') {
                message = 'Network error. Please check your connection.'
                type = 'network'
                canRetry = true
            }

            return {
                message,
                type,
                canRetry,
                originalError: error
            }
        },

        /**
         * Log API usage for debugging (development only)
         *
         * @param {string} action - The action being performed
         * @param {object} data - Additional data to log
         */
        logApiUsage(action, data = {}) {
            // API usage logging removed for production
        },

        /**
         * Setup axios interceptors for common functionality
         * Call this in component mounted() if needed
         */
        setupApiInterceptors() {
            // Request interceptor
            axios.interceptors.request.use(
                (config) => {
                    this.logApiUsage('Request', {
                        url: config.url,
                        method: config.method
                    })
                    return config
                },
                (error) => {
                    this.logApiUsage('Request Error', { error: error.message })
                    return Promise.reject(error)
                }
            )

            // Response interceptor
            axios.interceptors.response.use(
                (response) => {
                    this.logApiUsage('Response', {
                        status: response.status,
                        cached: response.data && response.data.cached
                    })
                    return response
                },
                (error) => {
                    this.logApiUsage('Response Error', {
                        status: error.response && error.response.status,
                        message: error.message
                    })
                    return Promise.reject(error)
                }
            )
        }
    }
}



// WEBPACK FOOTER //
// ./src/Assets/components/AiAgents/mixins/aiFaqApi.js